import $ from 'jquery'
import _ from 'lodash'
import template from './metric-comparison-step1.hbs'
import OrgGardener from 'scoreboard/organizations/organizations-gardener'
import MultiTreeSelector from 'scoreboard/components/multi-tree-selector'
import i18n from 'scoreboard/components/i18n'
import enums from 'scoreboard/components/enums'
import constants from 'scoreboard/components/constants'
import select from 'scoreboard-select'
import dropdown from 'scoreboard/components/dropdown'
import globals from 'scoreboard/components/globals'
import MetricComparisonModel from './metric-comparison-model'
import conductor from 'scoreboard/components/conductor'

var noMetricSelectionLevels = _.memoize(function() {
	return _.pick(enums.orgSelectionLevel, 'only-children', 'only-descendants')
})

function selectionLevels(treeItem) {
	var hasMetrics = _.indexOf(treeItem.scorecardItemTypes || [], 'metric') != -1
	return hasMetrics ? enums.orgSelectionLevel : noMetricSelectionLevels()
}

function childIds(tree, parent, descendants) {
	var children = tree.children(parent) || []
	if (children.length == 0) {
		return []
	}
	var ids = _.map(children, 'id')
	if (descendants) {
		//if including descendants, recursively call itself to gather the ids of
		//each child of this node. Upon each call to the child node it will return
		//an array of ids for its children, which we then flatten to a single
		//array of ids that we can concat to the list of ids to return
		ids = ids.concat(_.flatMap(children, _.partial(childIds, tree, _, descendants)))
	}
	return ids
}

var computeOrgIds = {
	'this-organization': function(tree, id) {
		return [id]
	},
	'only-children': function(tree, id) {
		return childIds(tree, tree.get(id), false)
	},
	'only-descendants': function(tree, id) {
		return childIds(tree, tree.get(id), true)
	},
	'organization-and-children': function(tree, id) {
		return [id].concat(childIds(tree, tree.get(id), false))
	},
	'organization-and-descendants': function(tree, id) {
		return [id].concat(childIds(tree, tree.get(id), true))
	}
}

// recursively determines if a parent or any of its descendants has a metric in
// its scorecard tree
var hasMetrics = function(tree, parent, cache) {
	if (!_.isNil(cache[parent.id])) {
		return cache[parent.id]
	}

	var result = [].concat(parent.scorecardItemTypes).indexOf('metric') != -1
	if (!result) {
		var children = tree.children(parent) || []
		result = _.some(children, _.partial(hasMetrics, tree, _))
	}
	cache[parent.id] = result
	return result
}

var OrganizationsView = MultiTreeSelector.extend({

	className: 'report-metric-comparison metric-comparison-step1 ' +
		'tree-selector-form single-tree-selector two-column-details-form ' +
		MultiTreeSelector.prototype.className,

	_template: function(ctx) {
		return template
	},

	createGardener: function() {
		//include descendants in count, leaf nodes with no nodes cannot be selected
		return new OrgGardener({
			section: constants.SECTION_SCORECARDS,
			itemTypes: true,
			descendants: true,
			permissions: true,
			ordered: true,
			id: globals.orgId,
			finderOptions: {
				type: 'light'
			},
			// #27617 show project selector only if this is for a filter in
			// admin/mass-edit
			showProjectSelector: conductor.section() === 'admin'
		})
	},

	initialize: function(options) {
		this.options = options || {}

		// prevent orgs that have no metrics in the org AND any of its descendants
		this.options.treeActions = {
			addable: function(node) {
				var treeItem = this.gardener.tree.get(node.data('id'))
				return hasMetrics(this.gardener.tree, treeItem, this.hasMetricCache)
			}.bind(this)
		}
		this.organizationSelections = this.oldSelections()

		//cache to hold resolution of whether nodes have metrics
		this.hasMetricCache = {}

		MultiTreeSelector.prototype.initialize.call(this, this.options)

		//when editing we may only have the unresolved selected orgs, to ensure
		//we have the fully resolved set, go ahead and find the computed org ids
		this.gardener.stream.once('end', function() {
			var tree = this.gardener.tree
			let numberOfSelections = _.size(this.organizationSelections)
			this.computedIds = _.transform(this.organizationSelections, function(result, level, orgId) {
				let org = tree.get(orgId)
				//also check if the org id that was persisted still references a
				//valid selection (i.e. it or descendants has metrics)
				if (org && hasMetrics(tree, org, this.hasMetricCache)) {
					result[orgId] = computeOrgIds[level](tree, orgId)
				} else {
					//invalid org selection, remove it
					delete this.organizationSelections[orgId]
				}
			}.bind(this), {})
			this.model.set('deducedOrgIds', _.flatten(_.values(this.computedIds)))
			if (numberOfSelections != _.size(this.organizationSelections)) {
				this.model.set('organizationSelections', this.organizationSelections)
			}
		}.bind(this))

		//capture org selection info
		this.listenTo(this.formView, 'change', function(e) {
			//determine the correct id the change event is referencing
			var tree = this.gardener.tree
			var id
			if (e && e.target) {
				//user click on the org selection set dropdown
				id = $(e.target).closest('[data-id]').data('id')
			} else {
				var treeItem = tree.selected()
				id = treeItem.id
				//capture the org name, to update the form listing when editing
				let orgLabels = this.model.get('orgLabels') || {}
				orgLabels[id] = treeItem.label
				this.model.set('orgLabels', orgLabels)
			}
			delete this.organizationSelections[id]
			delete this.computedIds[id]

			//determine the org selection set
			var select = this.$('tr[data-id='+id+'] select.level')
			if (select.length) {
				var level = select.val()
				this.organizationSelections[id] = level
				this.computedIds[id] = computeOrgIds[level](tree, id)
			}

			this.model.set('deducedOrgIds', _.flatten(_.values(this.computedIds)))
			this.model.set('organizationSelections', _.clone(this.organizationSelections))
		}.bind(this))

		//handle updating the table message as user select/deselect orgs
		this.listenTo(this.model, 'change:organizationSelections', () => {
			MetricComparisonModel.resolveOrgs(this.model).then(() => {
				this.toggleTableMessage()
			})
		})

		//handle when metric org resolution doesn't return an org with metrics
		this.listenTo(this.model, 'change:orgAndScorecardRoot', function (model, value) {
			var orgIds = this.model.get('deducedOrgIds') || []
			this.$('.orgs-no-metrics').toggleClass('is-not-displayed', !_.isEmpty(value) || orgIds.length == 0)
		}.bind(this))
	},

	oldSelections: function() {
		return this.model.get('organizationSelections') || {}
	},

	initItemData: function(treeItem) {
		//note the 'cascade' property for this view is the org selection level
		//for the org (e.g. 'this-organization', 'only-children').  The default
		//value for this is whatever show up as the first option the dropdown
		//therefore we omit this property for the item data when first selected
		return _.omit(MultiTreeSelector.prototype.initItemData.apply(this, arguments), 'cascade')
	},

	rowTemplate: function(ctx) {
		var treeItem = this.gardener.tree.get(ctx.id)
		let selection = _.extend(ctx, {selection: selectionLevels(treeItem)}).selection
		let content = $(MultiTreeSelector.prototype.rowTemplate.apply(this, arguments))
		content.addClass('has-select')
			.find('td:first-child')
			.append('<select class="level"></select>')
		dropdown.object(content.find('select'), selection)
		return content
	},

	_buildRow: function(data) {
		var row = MultiTreeSelector.prototype._buildRow.apply(this, arguments)
		let selectNode = row.find('select')
		if (data.cascade) {
			//when building a row, apply the org selection set (i.e this-org, only-children)
			//if previously known
			selectNode.val(data.cascade)
		}
		select(selectNode[0])
		return row
	},

	'delete': function(e) {
		var id = $(e.currentTarget).closest('tr').data('id')
		delete this.organizationSelections[id]
		delete this.computedIds[id]
		this.model.set('deducedOrgIds', _.flatten(_.values(this.computedIds)))
		this.model.set('organizationSelections', _.clone(this.organizationSelections))
		MultiTreeSelector.prototype.delete.apply(this, arguments)
	},

	toggleTableMessage: function() {
		var orgIds = this.model.get('deducedOrgIds') || []
		var minSelected = orgIds.length > 1
		this.$('.none-selected').toggleClass('is-not-displayed', minSelected)
	},

	context: function() {
		return {
			emptyTreeMessage: i18n.get('common.organization.noOrganizations')
		}
	}

})

export default OrganizationsView

OrganizationsView.__filename__ = 'reports/wizard/metric-comparison/metric-comparison-step1.js'
